/**
 * Game-level step logic — mirror of RunnerGame._resolveStep / _handle / _modifierStep / _close without contract I/O.
 * Movement comes from the pinned engine rules; money moves only from the run's reserved road budget (change order 4.0
 * §2) and is returned as effects so the contracts (or the demo world) carry them out.
 */
import { step } from './movement.js';
import {
  type CourseObject,
  type Crossing,
  EncounterKind,
  type HandlerParams,
  type MovementParams,
  type RoadParams,
  Route,
  type RunnerState,
  initialState,
} from './types.js';
import { stateHash } from './hash.js';

export const RoadEffectKind = { WATER: 1, OVERHEAT: 2, EXIT_HOLDERS: 3, EXIT_FORGE: 4 } as const;
export const ModifierEndReason = { STEPS: 1, CAP: 2, WATER: 3, RUN_ENDED: 4 } as const;

/** The run's escrow as RunnerTreasury tracks it, plus the game's Overheat bias state. */
export interface RoadState {
  budget: bigint;
  assigned: bigint;
  toHolders: bigint;
  toForge: bigint;
  /** Global counter in RunnerGame (persists across runs). */
  modifierCount: bigint;
  activeModifierId: bigint;
  modifierStepsLeft: bigint;
  modifierAssigned: bigint;
  open: boolean;
  exitRoute: number;
  returned: bigint;
}

export function initialRoad(budget: bigint, modifierCount = 0n): RoadState {
  return {
    budget,
    assigned: 0n,
    toHolders: 0n,
    toForge: 0n,
    modifierCount,
    activeModifierId: 0n,
    modifierStepsLeft: 0n,
    modifierAssigned: 0n,
    open: true,
    exitRoute: Route.NEUTRAL,
    returned: 0n,
  };
}

export type Effect =
  | { type: 'road'; effect: number; requested: bigint; assigned: bigint; toHolders: boolean }
  | { type: 'modifierStarted'; id: bigint; sliceBps: bigint; cap: bigint; steps: bigint }
  | { type: 'modifierEnded'; id: bigint; reason: number }
  | { type: 'releaseDeferred' }
  | { type: 'shoeOutcome'; collected: boolean; objectIndex: number }
  | { type: 'forgeAuthorize'; maxAmount: bigint; deadline: bigint }
  | { type: 'finish' }
  | { type: 'closed'; exitRoute: number; returned: bigint };

export interface StepRecord {
  stepIndex: number;
  window: bigint;
  net: bigint;
  volume: bigint;
  u: bigint;
  pace: bigint;
  before: RunnerState;
  state: RunnerState;
  stateHash: `0x${string}`;
  crossings: Crossing[];
  effects: Effect[];
  road: RoadState;
  finished: boolean;
}

export interface RunParams {
  m: MovementParams;
  h: HandlerParams;
  road: RoadParams;
  refSupply: bigint;
  /** Engine rules version pinned by the run's configuration (1 = RunEngineV1, 2 = RunEngineV2). */
  rules: number;
}

/** Stateful mirror of one funded RUNNER run, advanced one closed window at a time. */
export class RunSimulator {
  state: RunnerState = initialState();
  cursor = 0;
  stepIndex = 0;
  finished = false;
  readonly records: StepRecord[] = [];

  constructor(
    readonly course: CourseObject[],
    readonly params: RunParams,
    readonly startWindow: bigint,
    readonly maxSteps: number,
    public road: RoadState = initialRoad(params.road.runBudget),
    readonly shoeIndex: number | null = null,
    readonly fundingPaused = false,
  ) {}

  private assign(rd: RoadState, effects: Effect[], effect: number, toHolders: boolean, want: bigint): bigint {
    const left = rd.budget - rd.assigned;
    const got = want < left ? want : left;
    rd.assigned += got;
    if (toHolders) rd.toHolders += got;
    else rd.toForge += got;
    effects.push({ type: 'road', effect, requested: want, assigned: got, toHolders });
    return got;
  }

  private endModifier(rd: RoadState, effects: Effect[], reason: number): void {
    effects.push({ type: 'modifierEnded', id: rd.activeModifierId, reason });
    rd.activeModifierId = 0n;
    rd.modifierStepsLeft = 0n;
  }

  /**
   * @param now the step's window close time, `windowStart(startWindow + stepIndex + 1)` — used only for the Forge
   *            authorization deadline, exactly like RunnerGame (not the time resolution happened).
   */
  resolve(net: bigint, volume: bigint, now: bigint): StepRecord {
    if (this.finished) throw new Error('run finished');
    const { m, h, road, refSupply, rules } = this.params;
    const before = this.state;
    const out = step(before, this.course, this.cursor, net, volume, refSupply, m, h, rules);
    this.state = out.state;
    this.cursor = out.cursor;
    const effects: Effect[] = [];
    let finished = false;
    let modifierStarted = false;
    const rd: RoadState = { ...this.road };

    for (const c of out.crossings) {
      if (c.kind === EncounterKind.BURNING_BARRICADE) {
        if (c.hit && rd.activeModifierId === 0n && road.overheatSliceBps > 0n) {
          rd.modifierCount += 1n;
          rd.activeModifierId = rd.modifierCount;
          rd.modifierStepsLeft = road.overheatBiasSteps;
          rd.modifierAssigned = 0n;
          modifierStarted = true;
          effects.push({
            type: 'modifierStarted',
            id: rd.activeModifierId,
            sliceBps: road.overheatSliceBps,
            cap: road.overheatCap,
            steps: road.overheatBiasSteps,
          });
        }
      } else if (c.kind === EncounterKind.WATER_STATION) {
        if (c.hit) {
          this.assign(rd, effects, RoadEffectKind.WATER, true, (rd.budget * road.waterSliceBps) / 10_000n);
          if (rd.activeModifierId !== 0n) this.endModifier(rd, effects, ModifierEndReason.WATER);
          effects.push({ type: 'releaseDeferred' });
        }
      } else if (c.kind === EncounterKind.GOLDEN_SHOE) {
        if (this.shoeIndex !== null && c.index === this.shoeIndex) {
          effects.push({ type: 'shoeOutcome', collected: c.hit, objectIndex: c.index });
        }
      } else if (c.kind === EncounterKind.FORGE_GATE) {
        if (c.hit && !this.fundingPaused) {
          effects.push({ type: 'forgeAuthorize', maxAmount: h.forgeGateMax, deadline: now + h.forgeAuthSeconds });
        }
      } else if (c.kind === EncounterKind.FINISH) {
        finished = true;
        effects.push({ type: 'finish' });
        if (h.finishReleasesDeferred) effects.push({ type: 'releaseDeferred' });
      }
    }

    const idx = this.stepIndex;
    this.stepIndex += 1;
    if (finished || this.stepIndex >= this.maxSteps) {
      if (rd.activeModifierId !== 0n) this.endModifier(rd, effects, ModifierEndReason.RUN_ENDED);
      this.finished = true;
      // _close: the remaining budget follows the exit route only when the finish was reached.
      const route = this.state.route;
      rd.exitRoute = route;
      if (finished) {
        const left = rd.budget - rd.assigned;
        const toForge = route === Route.FORGE ? left : route === Route.HARVEST ? 0n : left / 2n;
        if (left - toForge > 0n) this.assign(rd, effects, RoadEffectKind.EXIT_HOLDERS, true, left - toForge);
        if (toForge > 0n) this.assign(rd, effects, RoadEffectKind.EXIT_FORGE, false, toForge);
      }
      rd.returned = rd.budget - rd.assigned;
      rd.open = false;
      effects.push({ type: 'closed', exitRoute: route, returned: rd.returned });
    } else if (rd.activeModifierId !== 0n && !modifierStarted) {
      // _modifierStep
      const slice = (rd.budget * road.overheatSliceBps) / 10_000n;
      const capLeft = road.overheatCap - rd.modifierAssigned;
      const want = slice < capLeft ? slice : capLeft;
      rd.modifierAssigned += this.assign(rd, effects, RoadEffectKind.OVERHEAT, false, want);
      rd.modifierStepsLeft -= 1n;
      if (rd.modifierAssigned >= road.overheatCap) this.endModifier(rd, effects, ModifierEndReason.CAP);
      else if (rd.modifierStepsLeft === 0n) this.endModifier(rd, effects, ModifierEndReason.STEPS);
    }
    this.road = rd;

    const rec: StepRecord = {
      stepIndex: idx,
      window: this.startWindow + BigInt(idx),
      net,
      volume,
      u: out.u,
      pace: out.pace,
      before,
      state: this.state,
      stateHash: stateHash(this.state),
      crossings: out.crossings,
      effects,
      road: rd,
      finished: this.finished,
    };
    this.records.push(rec);
    return rec;
  }
}

/** Mirrors RunnerGame.startRun maxSteps and shoe-cutoff arithmetic. */
export function runLimits(length: bigint, m: MovementParams, h: HandlerParams): { maxSteps: number; maxPace: bigint; minPace: bigint } {
  const maxPace = m.basePace + m.paceBonusMax;
  const slowest = h.barrierSlowPct > h.overheatSlowPct ? h.barrierSlowPct : h.overheatSlowPct;
  const minPace = (m.basePace * (100n - slowest)) / 100n;
  return { maxSteps: Number(length / minPace + 2n), maxPace, minPace };
}

export function earliestCrossingStep(y: bigint, maxPace: bigint): bigint {
  return (y + maxPace - 1n) / maxPace - 1n;
}
